import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useMatch } from 'react-router-dom'
import { motion, AnimatePresence, Variants } from 'framer-motion'

import { Box, Typography, useTheme, IconButton, Button, alpha } from '@mui/material'
import BoltIcon from '@mui/icons-material/Bolt'
import CloseIcon from '@mui/icons-material/Close'

import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { MESSAGES } from '@/constants/messages'

import { useActiveSession } from '@/contexts/ActiveSessionContext'
import { useDisclosure } from '@/hooks/useDisclosure'

import {
  ACTIVE_BANNER_VISIBLE_STATUSES,
  CHARGING_FINISHED_STATUSES,
  ChargingSessionInAppNotificationStatus,
} from '@/lib/in-app-notifications/categories/charging_session'

import { ChargingSessionSummaryModal } from '@/modules/ChargingPoint/modals/ChargingSessionSummaryModal'

import { AppRoutes } from '@/routes/types/routes'
import { routeParser } from '@/routes/helpers/parser'

import { ChargingSummary } from '@/types/Charging'
import { Logger } from '@/lib/logger'
import { numericUtils } from '@/utils/numeric'
import BatteryLevelBar from '@/components/BatteryLevelBar'

const logger = new Logger('ActiveSessionBanner')

const STORAGE_KEY_MINIMIZED = '@mred:session_banner_minimized'
const STORAGE_KEY_POSITION = '@mred:session_banner_position'

const MotionBox = motion(Box)

const pulseVariants: Variants = {
  pulse: {
    scale: [0.95, 1.05, 0.95],
    opacity: [0.6, 1, 0.6],
    transition: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
  },
}

const metricValueSx = {
  fontSize: 'clamp(1rem, 5.2vw, 1.3rem)',
  fontWeight: 800,
  lineHeight: 1,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
} as const

const metricUnitSx = {
  fontSize: 'clamp(0.65rem, 2.8vw, 0.8rem)',
  fontWeight: 700,
  lineHeight: 1,
  whiteSpace: 'nowrap',
} as const

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

const triggerHaptic = () => {
  Haptics.impact({ style: ImpactStyle.Light }).catch(() => {})
}

export const ActiveSessionBanner: React.FC = () => {
  const activeSession = useActiveSession()
  const { notification, connected, dismissSession } = activeSession

  const chargingSummaryModal = useDisclosure<ChargingSummary>()

  const navigate = useNavigate()
  const isInsideChargingPointScreen = useMatch(AppRoutes.CHARGING_POINT)
  const theme = useTheme()
  const constraintsRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)

  const [isMinimized, setIsMinimized] = useState<boolean>(false)
  const [bubblePos, setBubblePos] = useState<{ x: number; y: number } | null>(null)
  const [hasMounted, setHasMounted] = useState(false)

  const sessionData = notification?.data?.payload
  const notificationData = notification?.data

  useEffect(() => {
    const storedPos = localStorage.getItem(STORAGE_KEY_POSITION)

    if (storedPos) {
      try {
        setBubblePos(JSON.parse(storedPos))
      } catch (e) {
        logger.warn('Failed to parse bubble position', e)
      }
    }
    setHasMounted(true)
  }, [])

  useEffect(() => {
    if (hasMounted) {
      localStorage.setItem(STORAGE_KEY_MINIMIZED, String(isMinimized))
    }
  }, [isMinimized, hasMounted])

  useEffect(() => {
    if (hasMounted && bubblePos) {
      localStorage.setItem(STORAGE_KEY_POSITION, JSON.stringify(bubblePos))
    }
  }, [bubblePos, hasMounted])

  if (!sessionData || !notificationData || !hasMounted) return null
  if (isInsideChargingPointScreen) return null
  if (!ACTIVE_BANNER_VISIBLE_STATUSES.includes(sessionData.status)) {
    return null
  }

  const isFinished = CHARGING_FINISHED_STATUSES.includes(sessionData.status as ChargingSessionInAppNotificationStatus)

  const handleToggleMinimize = (e?: React.SyntheticEvent) => {
    e?.stopPropagation()

    if (isDraggingRef.current) return

    triggerHaptic()
    setIsMinimized((prev) => !prev)
  }

  const handleDeepLink = async () => {
    if (!sessionData.station_id || !sessionData.charging_point_id) return

    const path = routeParser(AppRoutes.CHARGING_POINT, {
      stationId: String(sessionData.station_id),
      chargingPointId: String(sessionData.charging_point_id),
    })

    if (isFinished) await dismissSession()
    navigate(path)
  }

  const handleSummary = () => {
    chargingSummaryModal.open(sessionData)
    setIsMinimized(true)
  }

  return (
    <>
      <Box
        ref={constraintsRef}
        sx={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: theme.zIndex.modal }}
      />

      <AnimatePresence mode="wait">
        {!isMinimized ? (
          <MotionBox
            key="expanded-card"
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.2}
            onDragEnd={(_, info) => {
              if (info.offset.y < -30) handleToggleMinimize()
            }}
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              zIndex: theme.zIndex.modal + 1,
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none',
              paddingTop: 'calc(env(safe-area-inset-top) + 8px)',
              touchAction: 'none',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Box
              onClick={handleDeepLink}
              sx={{
                pointerEvents: 'auto',
                width: '90%',
                maxWidth: 400,
                backgroundColor: alpha(theme.palette.grey[900], 0.85),
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                borderRadius: 4,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                boxShadow: theme.shadows[10],
                cursor: 'pointer',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      backgroundColor: isFinished ? theme.palette.success.main : theme.palette.primary.main,
                      aspectRatio: '1/1',
                      flexShrink: 0,
                    }}
                  >
                    <MotionBox
                      variants={pulseVariants}
                      animate={!isFinished && connected ? 'pulse' : 'none'}
                      sx={{ display: 'flex' }}
                    >
                      <BoltIcon sx={{ fontSize: 14, color: theme.palette.common.white }} />
                    </MotionBox>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ color: theme.palette.common.white, fontWeight: 600 }}>
                      {notificationData.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: alpha(theme.palette.common.white, 0.6), lineHeight: 1.4, display: 'block' }}
                    >
                      {!connected ? MESSAGES.CHARGING_SESSION.OFFLINE_RECONNECTING : notificationData.body}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleToggleMinimize}
                  sx={{ color: alpha(theme.palette.common.white, 0.3) }}
                  aria-label="Minimizar notificação"
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {!isFinished && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'nowrap',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25, flexShrink: 0 }}>
                      <Typography sx={{ ...metricValueSx, color: theme.palette.common.white }}>
                        {numericUtils.formatKwh(sessionData.kwh_delivered)}
                      </Typography>
                      <Typography sx={{ ...metricUnitSx, color: alpha(theme.palette.common.white, 0.5) }}>
                        kWh
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        borderLeft: `1px solid ${theme.palette.divider}`,
                        height: 22,
                        opacity: 0.2,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25, flexShrink: 0 }}>
                      <Typography sx={{ ...metricValueSx, color: theme.palette.common.white }}>
                        {numericUtils.formatKw(sessionData.power_active)}
                      </Typography>
                      <Typography sx={{ ...metricUnitSx, color: alpha(theme.palette.common.white, 0.5) }}>
                        kW
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        borderLeft: `1px solid ${theme.palette.divider}`,
                        height: 22,
                        opacity: 0.2,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{ ...metricValueSx, fontWeight: 700, color: theme.palette.primary.light, flexShrink: 0 }}
                    >
                      {currencyFormatter.format(Number(sessionData.total_cost || 0))}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BatteryLevelBar
                      soc={sessionData.soc}
                      height={6}
                      color="inherit"
                      sx={{ flex: 1, color: theme.palette.primary.light }}
                    />
                    <Typography
                      sx={{
                        color: alpha(theme.palette.common.white, 0.7),
                        fontSize: 'clamp(0.72rem, 3vw, 0.85rem)',
                        fontWeight: 800,
                        lineHeight: 1,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {numericUtils.formatPercentage(sessionData.soc)}
                      {numericUtils.isValidNumericInput(sessionData.soc) && '%'}
                    </Typography>
                  </Box>
                </Box>
              )}

              {isFinished && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation()
                    handleSummary()
                  }}
                  sx={{ borderRadius: 2, backgroundColor: theme.palette.success.main }}
                >
                  Ver Resumo
                </Button>
              )}
            </Box>
          </MotionBox>
        ) : (
          <MotionBox
            key="floating-bubble"
            drag
            dragConstraints={constraintsRef}
            dragElastic={0.1}
            dragMomentum={false}
            onDragStart={() => {
              isDraggingRef.current = true
            }}
            onDragEnd={(_, info) => {
              const snapX = info.point.x < window.innerWidth / 2 ? 0 : window.innerWidth - 72
              setBubblePos({ x: snapX, y: info.point.y })
              setTimeout(() => {
                isDraggingRef.current = false
              }, 100)
            }}
            onTap={() => handleToggleMinimize()}
            initial={bubblePos || { x: window.innerWidth - 72, y: window.innerHeight - 200 }}
            animate={bubblePos ? { x: bubblePos.x, y: bubblePos.y, scale: 1, opacity: 1 } : { scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileTap={{ scale: 0.9 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              zIndex: theme.zIndex.modal + 1,
              touchAction: 'none',
              pointerEvents: 'auto',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: isFinished ? theme.palette.success.main : theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: theme.shadows[12],
                border: `2px solid ${alpha(theme.palette.common.white, 0.2)}`,
                cursor: 'grab',
                position: 'relative',
                '&:active': { cursor: 'grabbing' },
                ...(!isFinished &&
                  connected && {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      backgroundColor: 'inherit',
                      animation: 'pulse 2s infinite',
                      opacity: 0.4,
                      zIndex: -1,
                    },
                  }),
              }}
            >
              <MotionBox variants={pulseVariants} animate={!isFinished && connected ? 'pulse' : 'none'}>
                <BoltIcon sx={{ color: theme.palette.common.white, fontSize: 24 }} />
              </MotionBox>

              {!isFinished && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -8,
                    backgroundColor: alpha(theme.palette.common.black, 0.85),
                    backdropFilter: 'blur(4px)',
                    borderRadius: '8px',
                    px: 1,
                    py: 0.2,
                    border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.common.white, fontWeight: 800, fontSize: '0.6rem' }}
                  >
                    {numericUtils.formatKwh(sessionData.kwh_delivered)} kWh
                  </Typography>
                </Box>
              )}
            </Box>
          </MotionBox>
        )}
      </AnimatePresence>
      <ChargingSessionSummaryModal modalMethods={chargingSummaryModal} modalTitle="Sessão finalizada" />
    </>
  )
}
